import dayjs, { Dayjs } from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import AdvancedFormat from 'dayjs/plugin/advancedFormat'
import WeekOfYear from 'dayjs/plugin/weekOfYear'
import 'dayjs/locale/de'
import 'dayjs/locale/en'

dayjs.extend(LocalizedFormat)
dayjs.extend(AdvancedFormat)
dayjs.extend(WeekOfYear)
// Preserve the historic global default for legacy direct dayjs().format() callers.
dayjs.locale('de')
const ISO_DATE_FORMAT = 'YYYY-MM-DD'
type MonthYearFormat = 'numeric' | 'short' | 'long'

const isUsEnglishLocale = (locale: string) => locale === 'en-US'

const getLocalizedNumericDateFormat = (locale: string) => (isUsEnglishLocale(locale) ? 'MM/DD/YYYY' : 'DD.MM.YYYY')

export function getLocalizedDateString(date: Dayjs | string, withTime?: boolean, locale = 'de-DE'): string {
    return dayjs(date)
        .locale(locale)
        .format(withTime ? 'LLLL' : getLocalizedNumericDateFormat(locale))
}

export function toIsoDateString(date: Dayjs | string | Date): string {
    if (typeof date === 'string') {
        return dayjs(date).format(ISO_DATE_FORMAT)
    }
    if (date instanceof Date) {
        return dayjs(date).format(ISO_DATE_FORMAT)
    }
    return date.format(ISO_DATE_FORMAT)
}

/**
 * Converts an ISO date string to a localized date string
 */
export function isoDateStringToLocalFormat(isoDateString?: string, withTime?: boolean, locale = 'de-DE') {
    return isoDateString ? getLocalizedDateString(dayjs(isoDateString), withTime, locale) : undefined
}

export function isoDateStringToLocalizedMonthYear(isoDateString?: string, monthYearFormat: MonthYearFormat = 'numeric', locale = 'de-DE') {
    if (!isoDateString) {
        return undefined
    }

    const format =
        monthYearFormat === 'long'
            ? 'MMMM YYYY'
            : monthYearFormat === 'short'
              ? 'MMM YYYY'
              : isUsEnglishLocale(locale)
                ? 'MM/YYYY'
                : 'MM.YYYY'

    return dayjs(isoDateString).locale(locale).format(format)
}

export function isoDateStringToLocalizedTime(isoDateString: string, locale = 'de-DE') {
    return dayjs(isoDateString).locale(locale).format('LT')
}

export function isoDateToTimeIfTodayOrDate(isoDateString: string, locale = 'de-DE') {
    const date = dayjs(isoDateString)
    const today = dayjs().startOf('day')

    return date.isSame(today, 'day') ? isoDateStringToLocalizedTime(isoDateString, locale) : getLocalizedDateString(date, false, locale)
}

export function isDateToday(isoDateString: string): boolean {
    const date = dayjs(isoDateString)
    const today = dayjs()

    return date.isSame(today, 'day')
}

export function localFormatToIsoDateString(localFormat?: string) {
    return localFormat ? toIsoDateString(dayjs(localFormat)) : undefined
}

type Difference = {
    text: string
    days: number
}
export function dateDifference({ date, maxDaysToDisplay }: { date: string; maxDaysToDisplay?: number }): Difference {
    const today = dayjs().startOf('day')
    const daysRemaining = dayjs(date).diff(today, 'day')

    if (isNaN(daysRemaining)) {
        throw new InvalidDateInputError(`invalid date received ${date}`)
    }

    const days = daysRemaining === 0 ? 'Heute' : daysRemaining.toString()

    const unit = Math.abs(daysRemaining) === 1 ? 'Tag' : 'Tage'
    const daysString = maxDaysToDisplay && daysRemaining > maxDaysToDisplay ? `>${maxDaysToDisplay}` : days

    return { text: daysRemaining === 0 ? days : `${daysString} ${unit}`, days: daysRemaining }
}

export function remainingYears(deadline: Dayjs | string, date: Dayjs | string): number {
    const remainingYears = dayjs(deadline).diff(dayjs(date), 'years', true)

    if (isNaN(remainingYears)) {
        throw new InvalidDateInputError(`invalid date received ${deadline} or ${date}`)
    }

    return remainingYears
}

export class InvalidDateInputError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'InvalidInputError' // Ensure descriptive error name
    }
}

export type DateString = string
export type DateTimeString = string

export const getMonthName = (monthNumber: number, locale = 'de-DE') => {
    const date = new Date()
    date.setMonth(monthNumber - 1)

    return date.toLocaleString(locale, { month: 'long' })
}

export const isValidDayjsDateOrNull = (date: Dayjs | null) => date === null || date.isValid()
